import { Link as LinkIcon } from '@mui/icons-material';
import { InputAdornment, Link, Stack, TextField, Typography } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

interface LinkInputProps {
    value: string;
    placeholder?: string;
    disabled?: boolean;
    helperText?: string;
    size?: 'medium' | 'small' | 'verysmall';
    onChange?: (val: string) => void;
}

/**
 * Modern LinkInput component with MUI styling and URL validation.
 * @param {LinkInputProps} props - The props for the link input field.
 * @returns {JSX.Element} The rendered link input field.
 */
function LinkInput({ value, placeholder, disabled, size, helperText, onChange }: LinkInputProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).skills;

    const innerSize = size || 'medium';
    const inputSize = innerSize === 'medium' || innerSize === 'small' ? innerSize : 'small';

    const isValid = /((https?:\/\/.+)|(^$))/i.test(value);
    const showLink = isValid && value && onChange;

    if (!onChange) {
        return (
            <Typography variant="body2" sx={{ p: 1 }}>
                {value}
            </Typography>
        );
    }

    return (
        <Stack spacing={1}>
            <TextField
                variant="outlined"
                disabled={disabled}
                size={inputSize}
                value={value}
                placeholder={placeholder}
                multiline
                error={!isValid}
                helperText={helperText || (isValid ? undefined : langStrings.invaildURL)}
                onChange={(event) => onChange(event.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <LinkIcon fontSize="small" color={isValid ? 'primary' : 'error'} />
                        </InputAdornment>
                    ),
                }}
                sx={{
                    width: 'auto',
                    minWidth: innerSize === 'verysmall' ? '160px' : innerSize === 'small' ? '200px' : '240px',
                    '& .MuiInputBase-input': {
                        fontSize: '0.875rem',
                        ...(innerSize === 'verysmall' && { padding: '6px 8px' }),
                    },
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                    },
                }}
            />
            {showLink && (
                <Link
                    href={value}
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                        fontSize: '0.875rem',
                        ml: 0.5,
                    }}
                >
                    {langStrings.checkLink}
                </Link>
            )}
        </Stack>
    );
}

export default LinkInput;
